import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { switchMap, take } from 'rxjs';
import { selectModulesStatus, selectModulesStatusLastFetched } from '@lessor/core/store/subscription/subscription.selectors';
import { fetchModulesStatus } from '@lessor/core/store/subscription/subscription.actions';
import { Actions, ofType } from '@ngrx/effects';
import { fetchModulesStatusFailed, fetchModulesStatusSuccess } from '@lessor/core/store/subscription/subscription.actions';
import { of } from 'rxjs';
import { ToastController } from '@ionic/angular';

const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export function moduleGuard(moduleKey: string): CanActivateFn {
  return () => {
    const store = inject(Store);
    const router = inject(Router);
    const actions$ = inject(Actions);
    const toastController = inject(ToastController);

    const lastFetched = store.selectSignal(selectModulesStatusLastFetched)();
    const now = Date.now();
    const isCacheValid = lastFetched && (now - lastFetched) < CACHE_DURATION_MS;

    if (isCacheValid) {
      const modules = store.selectSignal(selectModulesStatus)();
      const mod = modules.find(m => m.key === moduleKey);
      if (mod?.active) {
        return true;
      }
      showModuleInactiveToast(toastController, mod?.name ?? moduleKey, router);
      return false;
    }

    store.dispatch(fetchModulesStatus());

    return actions$.pipe(
      ofType(fetchModulesStatusSuccess, fetchModulesStatusFailed),
      take(1),
      switchMap((action) => {
        if (action.type === fetchModulesStatusFailed.type) {
          return of(true);
        }

        const modules = store.selectSignal(selectModulesStatus)();
        const mod = modules.find(m => m.key === moduleKey);
        if (mod?.active) {
          return of(true);
        }
        showModuleInactiveToast(toastController, mod?.name ?? moduleKey, router);
        return of(false);
      })
    );
  };
}

async function showModuleInactiveToast(
  toastController: ToastController,
  moduleName: string,
  router: Router
): Promise<void> {
  const toast = await toastController.create({
    message: `O módulo "${moduleName}" não está ativo na sua assinatura. Contrate na tela de Assinaturas.`,
    duration: 4000,
    position: 'bottom',
    color: 'warning',
    buttons: [
      {
        text: 'Contratar',
        handler: () => {
          router.navigateByUrl('/subscription');
        },
      },
    ],
  });
  await toast.present();
}
